import { useCallback } from "react";
import { useDialogs } from "@toolpad/core";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";

import type { FindingRow } from "../types";
import type { MenuCallbacks } from "~/components/FindingStatusIndicator";
import { isSpaceOrWorkspaceScope, useScope } from "~/hooks/useScope";
import { copyToClipboard } from "~/lib/clipboard";
import { buildFindingUrl } from "~/lib/findingUrl";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { sortFindingsToExceptionEntitiesMrns } from "~/pages/inventory/components/Findings/FindingsTable/utils/findingTableUtils";
import { SetExceptionDialog } from "~/components/exceptions/SetExceptionDialog";
import { useTickets } from "~/pages/tickets/hooks/useTickets";
import { CreateTicketDialog } from "~/pages/tickets/components/CreateTicketDialog";
import type { CreateTicketDialogPayload } from "~/pages/tickets/components/CreateTicketDialog/CreateTicketDialog";
import {
  CreateSecurityPipelinePrDialog,
  type CreateSecurityPipelinePrDialogPayload,
} from "~/pages/integrations/security-pipeline/components/CreatePullRequest/CreateSecurityPipelinePRDialog";
import { getExceptionLink } from "~/pages/findings/Exceptions/utils";
import { getTicketDetailsUrl } from "~/lib/ticketUtils";
import {
  GetFindingsDocument,
  GetAssetInsightsDocument,
  LoadAssetDocument,
} from "~/operations";
import { useCanApplyExceptionMutation } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/ApplyExceptionMutation";
import {
  buildSelectedEntity,
  buildExceptionTargetData,
  getFirstCaseMrn,
  isFixableFinding,
} from "./findingActionUtils";

export type UseFindingActionsParams = {
  finding: FindingRow;
  assetId: string;
  spaceId: string;
  jobId?: string;
  assetMrn?: string;
  assetName?: string;
};

export function useFindingActions({
  finding,
  assetId,
  spaceId,
  jobId,
  assetMrn,
  assetName,
}: UseFindingActionsParams): MenuCallbacks {
  const dialogs = useDialogs();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { activeScope, spaceScope } = useScope();
  const canSetException = useCanApplyExceptionMutation(activeScope);

  const selectedEntity = buildSelectedEntity(finding);

  const { handleSetException } = useExceptions({
    scopeMrns: [assetMrn || ""],
    ...sortFindingsToExceptionEntitiesMrns({
      selectedEntities: [selectedEntity],
    }),
    refetchQueries: [
      GetFindingsDocument,
      GetAssetInsightsDocument,
      LoadAssetDocument,
    ],
  });

  const {
    handleCreateCase,
    hasCreateTicketPermissions,
    loading: createTicketLoading,
  } = useTickets({
    createCaseRefetchQueries: [GetFindingsDocument],
  });

  const onCopyLink = useCallback(() => {
    const url = buildFindingUrl(finding, assetId, spaceId);
    copyToClipboard(`${window.location.origin}${url}`);
    enqueueSnackbar("Link copied to clipboard", { variant: "success" });
  }, [finding, assetId, spaceId, enqueueSnackbar]);

  const onViewDetails = useCallback(() => {
    const url = buildFindingUrl(finding, assetId, spaceId, jobId);
    navigate(url);
  }, [finding, assetId, spaceId, jobId, navigate]);

  const onSetException = useCallback(() => {
    dialogs.open(SetExceptionDialog, {
      target: "asset",
      selectedEntities: [selectedEntity],
      onSave: handleSetException,
      shouldListEntities: true,
      targetData: buildExceptionTargetData(assetId, assetMrn, assetName),
    });
  }, [
    selectedEntity,
    handleSetException,
    assetName,
    assetMrn,
    assetId,
    dialogs,
  ]);

  const onViewException = useCallback(() => {
    if (!finding.exception?.exceptionId || !activeScope) return;
    const url = getExceptionLink({
      id: finding.exception.exceptionId,
      queryParams: activeScope.params,
    });
    navigate(url);
  }, [finding.exception, activeScope, navigate]);

  const onViewTicket = useCallback(() => {
    const caseMrn = getFirstCaseMrn(finding);
    if (!caseMrn || !activeScope) return;
    const url = getTicketDetailsUrl({ scope: activeScope, ticketMrn: caseMrn });
    navigate(url);
  }, [finding.cases, activeScope, navigate]);

  const onCreateTicket = useCallback(() => {
    if (!spaceScope || !isSpaceOrWorkspaceScope(activeScope)) return;

    const payload: CreateTicketDialogPayload = {
      isCombinedTicket: true,
      target: "asset",
      loading: createTicketLoading,
      onSave: (data) => handleCreateCase({ data }),
      space: spaceScope,
      scope: activeScope,
      hasCreateCasePermissions: hasCreateTicketPermissions,
      selectedEntities: [{ mrn: finding.mrn, scopeMrn: assetMrn || "" }],
    };

    dialogs.open(CreateTicketDialog, payload);
  }, [
    finding.mrn,
    assetMrn,
    spaceScope,
    activeScope,
    createTicketLoading,
    handleCreateCase,
    hasCreateTicketPermissions,
    dialogs,
  ]);

  const onFixFinding = useCallback(() => {
    if (!spaceScope) return;
    if (!isFixableFinding(finding)) return;

    const payload: CreateSecurityPipelinePrDialogPayload = {
      spaceScope,
      // FindingRow is a subset of CVEFindingsNode | AdvisoryFindingsNode.
      // The dialog only uses mrn and title from findings, so this cast is safe.
      findings: [
        finding,
      ] as unknown as CreateSecurityPipelinePrDialogPayload["findings"],
      ...(assetMrn ? { assetMrns: [assetMrn] } : {}),
    };

    dialogs.open(CreateSecurityPipelinePrDialog, payload);
  }, [finding, spaceScope, assetMrn, dialogs]);

  const onViewFix = useCallback(() => {
    enqueueSnackbar("View fix is coming soon", { variant: "info" });
  }, [enqueueSnackbar]);

  return {
    onCopyLink,
    onViewDetails,
    onSetException,
    onViewException,
    onViewTicket,
    onCreateTicket,
    onFixFinding,
    onViewFix,
    canCreateTicket: hasCreateTicketPermissions,
    canSetException,
  };
}
